import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {useAuth} from '../../auth'
import {Fields, ReportAttachment} from '../core/_models'
import {
  deleteReportAttachment,
  fetchReportAttachmentDownloadBlob,
  fetchReportAttachmentViewBlob,
  getCrewUnitById,
  listReportAttachments,
} from '../core/_requests'
import {TankOptionRow} from '../../Management/core/_models'
import {getTanksOption} from '../../Management/core/_requests'
import {
  BUNKER_ATTACHMENT_TYPE_OPTIONS,
  BUNKER_FUEL_TYPES,
  BUNKER_FIELD_LABEL_LOOKUP,
  BunkerRowAttachment,
  BunkerReceivedRow,
  createEmptyBunkerRow,
} from './bunkerReportSchema'
import {OP} from '../core/operationPermissions'
import {useOperationsAccess} from '../core/useOperationsAccess'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { toast } from 'react-toastify'

export type PendingReportAttachment = {
  id: string
  reportBlock: number
  documentName: string
  remarks: string
  file?: File
  fileName?: string
}

type TankStorageOption = {
  storage: string
  grades: string[]
}

const BUNKER_ATTACHMENT_TYPES = BUNKER_ATTACHMENT_TYPE_OPTIONS

interface Props {
  formSections: Fields[]
  lookupSections?: Fields[]
  values: Record<string, any>
  allValues?: Record<string, Record<string, any>>
  reportId?: number | null
  initialPendingAttachments?: PendingReportAttachment[]
  onPendingAttachmentsChange?: (attachments: PendingReportAttachment[]) => void
  onChange: (label: string, id: string, value: any, isUserInitiated?: boolean) => void
}

const normalize = (text: string) =>
  String(text || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

const normalizePendingReportBlock = (value: number | string | undefined) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

const pendingAttachmentFileName = (item: PendingReportAttachment) =>
  String(item.file?.name || item.fileName || '').trim()

const pendingAttachmentSignature = (item: PendingReportAttachment) =>
  [
    normalizePendingReportBlock(item.reportBlock),
    normalize(item.documentName),
    String(item.remarks || '').trim(),
    pendingAttachmentFileName(item),
    item.file ? 'file' : 'nofile',
  ].join('|')

const samePendingAttachments = (
  left: PendingReportAttachment[],
  right: PendingReportAttachment[]
) => {
  if (left.length !== right.length) return false
  return left.every(
    (item, index) => pendingAttachmentSignature(item) === pendingAttachmentSignature(right[index])
  )
}

const normalizePendingAttachments = (attachments?: PendingReportAttachment[]) =>
  (attachments || []).map((item) => ({
    ...item,
    reportBlock: normalizePendingReportBlock(item.reportBlock),
  }))

const dedupeNonEmptyStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const result: string[] = []

  for (const value of values) {
    const normalizedValue = String(value || '').trim()
    if (!normalizedValue) continue
    const key = normalizedValue.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push(normalizedValue)
  }

  return result
}

const buildStorageOptions = (rows: TankOptionRow[]): TankStorageOption[] => {
  const grouped = new Map<string, TankStorageOption>()

  for (const row of rows || []) {
    const storage = String(row?.tankName || '').trim()
    if (!storage) continue

    const key = storage.toLowerCase()
    if (!grouped.has(key)) {
      grouped.set(key, {storage, grades: []})
    }

    const grade = String(row?.grade || '').trim()
    if (grade) {
      grouped.get(key)!.grades.push(grade)
    }
  }

  return Array.from(grouped.values()).map((item) => ({
    storage: item.storage,
    grades: dedupeNonEmptyStrings(item.grades),
  }))
}

const ensureStorageOption = (
  options: TankStorageOption[],
  currentStorage: string,
  currentGrade: string
): TankStorageOption[] => {
  const storage = String(currentStorage || '').trim()
  if (!storage) return options

  const key = storage.toLowerCase()
  if (options.some((item) => item.storage.toLowerCase() === key)) {
    return options
  }

  const grade = String(currentGrade || '').trim()
  return [{storage, grades: grade ? [grade] : []}, ...options]
}

const ensureGradeOption = (options: string[], currentGrade: string): string[] => {
  const grade = String(currentGrade || '').trim()
  if (!grade) return options
  if (options.some((item) => item.toLowerCase() === grade.toLowerCase())) {
    return options
  }
  return [grade, ...options]
}

const parseRows = (raw: any): BunkerReceivedRow[] => {
  if (!raw) return [createEmptyBunkerRow()]

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(parsed) || parsed.length === 0) return [createEmptyBunkerRow()]

    const rows: BunkerReceivedRow[] = []

    parsed.forEach((r: any) => {
      const reportBlockRaw = Number(r?.reportBlock)
      const reportBlock = Number.isFinite(reportBlockRaw) && reportBlockRaw > 0 ? reportBlockRaw : 1
      const allocationOnly = Boolean(r?.allocationOnly)
      const dateUtc = String(r?.dateUtc ?? '')
      const timeUtc = String(r?.timeUtc ?? '')
      const port = String(r?.port ?? '')

      const hasNewShape =
        r?.fuelType !== undefined ||
        r?.quantity !== undefined ||
        r?.supplierName !== undefined ||
        r?.storage !== undefined

      if (hasNewShape) {
        const attachments = Array.isArray(r?.attachments)
          ? r.attachments
              .map((a: any) => ({
                documentName: String(a?.documentName || ''),
                fileName: String(a?.fileName || ''),
              }))
              .filter((a: BunkerRowAttachment) => a.fileName)
          : String(r?.attachmentFileName || '').trim()
          ? [
              {
                documentName: String(r?.attachmentType || ''),
                fileName: String(r?.attachmentFileName || ''),
              },
            ]
          : []

        rows.push({
          reportBlock,
          allocationOnly,
          dateUtc,
          timeUtc,
          port,
          fuelType: String(r?.fuelType ?? ''),
          quantity: String(r?.quantity ?? ''),
          storageQuantity: String(r?.storageQuantity ?? r?.quantity ?? ''),
          supplierName: String(r?.supplierName ?? ''),
          sulfurPercentage: String(r?.sulfurPercentage ?? ''),
          storage: String(r?.storage ?? ''),
          grade: String(r?.grade ?? r?.fuelType ?? ''),
          attachmentType: String(r?.attachmentType ?? ''),
          attachments,
        })
        return
      }

      // Legacy payload support: split one HFO/MGO row into per-fuel rows.
      const hfo = String(r?.hfo ?? '').trim()
      const mgo = String(r?.mgo ?? '').trim()

      if (hfo) {
        rows.push({
          reportBlock,
          allocationOnly,
          dateUtc,
          timeUtc,
          port,
          fuelType: 'HFO',
          quantity: hfo,
          storageQuantity: hfo,
          supplierName: String(r?.hfoSupplierName ?? ''),
          sulfurPercentage: String(r?.hfoSulfurPercentage ?? ''),
          storage: String(r?.hfoStore ?? r?.hfoStorage ?? ''),
          grade: String(r?.hfoGrade ?? 'HFO'),
          attachmentType: '',
          attachments: [],
        })
      }

      if (mgo) {
        rows.push({
          reportBlock,
          allocationOnly,
          dateUtc,
          timeUtc,
          port,
          fuelType: 'MGO',
          quantity: mgo,
          storageQuantity: mgo,
          supplierName: String(r?.mgoSupplierName ?? ''),
          sulfurPercentage: String(r?.mgoSulfurPercentage ?? ''),
          storage: String(r?.mgoStore ?? r?.mgoStorage ?? ''),
          grade: String(r?.mgoGrade ?? 'MGO'),
          attachmentType: '',
          attachments: [],
        })
      }

      if (!hfo && !mgo) {
        rows.push({
          reportBlock,
          allocationOnly,
          dateUtc,
          timeUtc,
          port,
          fuelType: '',
          quantity: '',
          storageQuantity: '',
          supplierName: '',
          sulfurPercentage: '',
          storage: '',
          grade: '',
          attachmentType: '',
          attachments: [],
        })
      }
    })

    return rows.length > 0 ? rows : [createEmptyBunkerRow()]
  } catch {
    return [createEmptyBunkerRow()]
  }
}

const bunkerRowsSignature = (rows: BunkerReceivedRow[]) =>
  JSON.stringify(
    rows.map((row) => ({
      reportBlock: normalizePendingReportBlock(row.reportBlock),
      allocationOnly: Boolean(row.allocationOnly),
      dateUtc: String(row.dateUtc || ''),
      timeUtc: String(row.timeUtc || ''),
      port: String(row.port || ''),
      fuelType: String(row.fuelType || ''),
      quantity: String(row.quantity || ''),
      storageQuantity: String(row.storageQuantity || ''),
      supplierName: String(row.supplierName || ''),
      sulfurPercentage: String(row.sulfurPercentage || ''),
      storage: String(row.storage || ''),
      grade: String(row.grade || ''),
      attachmentType: String(row.attachmentType || ''),
      attachments: (row.attachments || []).map((attachment) => ({
        documentName: String(attachment.documentName || ''),
        fileName: String(attachment.fileName || ''),
      })),
    }))
  )

const BunkerReportStep: FC<Props> = ({
  formSections,
  lookupSections,
  values,
  allValues,
  reportId,
  initialPendingAttachments,
  onPendingAttachmentsChange,
  onChange,
}) => {
  const {currentUser} = useAuth() as any
  const {opCan} = useOperationsAccess()
  const canReadReport = opCan(OP.READ_POSITION_REPORT)
  const canWriteReport =
    opCan(OP.CREATE_POSITION_REPORT) || opCan(OP.UPDATE_POSITION_REPORT)
  const pendingHydratedFromFieldRef = useRef(false)
  const lastSyncedAttachmentJsonRef = useRef('')
  const existingAttachmentsRef = useRef<ReportAttachment[]>([])
  const initialRowsHadDataRef = useRef(false)

  const [receivedRows, setReceivedRows] = useState<BunkerReceivedRow[]>(() => {
    const sections = lookupSections && lookupSections.length > 0 ? lookupSections : formSections
    const field = sections.find(
      (f) => normalize(f.label) === normalize(BUNKER_FIELD_LABEL_LOOKUP.receivedRows)
    )
    if (!field) return [createEmptyBunkerRow()]
    const key = String(field.id)
    let raw = values[key]?.value
    if ((raw === undefined || raw === null) && allValues) {
      for (const tabData of Object.values(allValues)) {
        const candidate = (tabData as any)?.[key]?.value
        if (candidate !== undefined && candidate !== null) {
          raw = candidate
          break
        }
      }
    }
    initialRowsHadDataRef.current = Boolean(raw)
    return parseRows(raw)
  })
  const [existingAttachments, setExistingAttachments] = useState<ReportAttachment[]>([])
  const [pendingAttachments, setPendingAttachments] = useState<PendingReportAttachment[]>(() =>
    normalizePendingAttachments(initialPendingAttachments)
  )
  const [attachmentDocNameByBlock, setAttachmentDocNameByBlock] = useState<Record<number, string>>({})
  const [attachmentRemarksByBlock, setAttachmentRemarksByBlock] = useState<Record<number, string>>({})
  const [attachmentFileByBlock, setAttachmentFileByBlock] = useState<Record<number, File | null>>({})
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  const [rowsHydrated, setRowsHydrated] = useState(() => initialRowsHadDataRef.current)
  const [storageOptions, setStorageOptions] = useState<TankStorageOption[]>([])
  existingAttachmentsRef.current = existingAttachments
  const todayYmd = useMemo(() => new Date().toISOString().slice(0, 10), [])

  const resolvedLookupSections = useMemo(
    () => (lookupSections && lookupSections.length > 0 ? lookupSections : formSections),
    [lookupSections, formSections]
  )

  const fieldMap = useMemo(() => {
    const findField = (label: string) =>
      resolvedLookupSections.find((field) => normalize(field.label) === normalize(label))

    const yearField = findField(BUNKER_FIELD_LABEL_LOOKUP.year)
    const vesselField = findField(BUNKER_FIELD_LABEL_LOOKUP.vessel)
    const quantityDateField = findField(BUNKER_FIELD_LABEL_LOOKUP.quantityDate)
    const quantityTimeField = findField(BUNKER_FIELD_LABEL_LOOKUP.quantityTime)
    const quantityHfoField = findField(BUNKER_FIELD_LABEL_LOOKUP.quantityHfo)
    const quantityMgoField = findField(BUNKER_FIELD_LABEL_LOOKUP.quantityMgo)
    const receivedRowsField = findField(BUNKER_FIELD_LABEL_LOOKUP.receivedRows)
    const attachmentRowsField = findField(BUNKER_FIELD_LABEL_LOOKUP.attachmentRows)

    const mappedIds = new Set(
      [
        yearField?.id,
        vesselField?.id,
        quantityDateField?.id,
        quantityTimeField?.id,
        quantityHfoField?.id,
        quantityMgoField?.id,
        receivedRowsField?.id,
        attachmentRowsField?.id,
      ].filter(Boolean)
    )

    const extraFields = formSections.filter((field) => !mappedIds.has(field.id))

    return {
      yearField,
      vesselField,
      quantityDateField,
      quantityTimeField,
      quantityHfoField,
      quantityMgoField,
      receivedRowsField,
      attachmentRowsField,
      extraFields,
    }
  }, [formSections, resolvedLookupSections])

  const getAllRawValuesByFieldId = (fieldId?: number | string) => {
    if (fieldId === undefined || fieldId === null) return [] as any[]
    const key = String(fieldId)
    const items: any[] = []

    const local = values[key]?.value
    if (local !== undefined && local !== null) items.push(local)

    if (allValues) {
      for (const tabData of Object.values(allValues)) {
        const candidate = (tabData as any)?.[key]?.value
        if (candidate !== undefined && candidate !== null) {
          items.push(candidate)
        }
      }
    }

    return items
  }

  const scoreRow = (row: BunkerReceivedRow) => {
    const valuesToScore = [
      row.dateUtc,
      row.timeUtc,
      row.port,
      row.fuelType,
      row.quantity,
      row.storageQuantity,
      row.supplierName,
      row.sulfurPercentage,
      row.storage,
      row.grade,
      row.attachmentType,
      String(row.attachments?.length || 0),
    ]
    return valuesToScore.reduce((acc, item) => (String(item || '').trim() ? acc + 1 : acc), 0)
  }

  const getRowFuelDescriptor = (row: BunkerReceivedRow) =>
    String(row.fuelType || row.grade || '').toUpperCase()

  const toNumeric = (value: string | number | null | undefined) => {
    const parsed = Number.parseFloat(String(value ?? ''))
    return Number.isFinite(parsed) ? parsed : 0
  }

  const getEffectiveAllocatedQuantity = (row: BunkerReceivedRow) => {
    const storageQty = toNumeric(row.storageQuantity)
    if (storageQty > 0) return storageQty
    return toNumeric(row.quantity)
  }

  const buildRowAllocationKey = (row: BunkerReceivedRow) =>
    [row.dateUtc, row.timeUtc, row.port, row.supplierName].map((v) => String(v || '').trim()).join('|')

  const getReportBlockIds = (rows: BunkerReceivedRow[]) => {
    const ids = Array.from(
      new Set(
        rows
          .map((row) => Number(row.reportBlock || 1))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    )
    return ids.length > 0 ? ids.sort((a, b) => a - b) : [1]
  }

  const pickBestReceivedRowsRaw = (fieldId?: number | string) => {
    const raws = getAllRawValuesByFieldId(fieldId)
    if (raws.length === 0) return undefined

    let bestRaw = raws[0]
    let bestScore = -1

    for (const raw of raws) {
      const rows = parseRows(raw)
      const score = rows.reduce((acc, row) => acc + scoreRow(row), 0)
      if (score > bestScore) {
        bestScore = score
        bestRaw = raw
      }
    }

    return bestRaw
  }

  const pickBestAttachmentPayloadRaw = (fieldId?: number | string) => {
    const raws = getAllRawValuesByFieldId(fieldId)
    if (raws.length === 0) return undefined

    const scorePayload = (raw: any) => {
      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        const uploaded = Array.isArray(parsed?.uploaded) ? parsed.uploaded.length : 0
        const pending = Array.isArray(parsed?.pending) ? parsed.pending.length : 0
        return uploaded + pending
      } catch {
        return 0
      }
    }

    return raws.sort((a, b) => scorePayload(b) - scorePayload(a))[0]
  }

  const bestReceivedRowsRaw = useMemo(
    () =>
      fieldMap.receivedRowsField ? pickBestReceivedRowsRaw(fieldMap.receivedRowsField.id) : undefined,
    [fieldMap.receivedRowsField?.id, values, allValues]
  )

  const bestAttachmentPayloadRaw = useMemo(
    () =>
      fieldMap.attachmentRowsField
        ? pickBestAttachmentPayloadRaw(fieldMap.attachmentRowsField.id)
        : undefined,
    [fieldMap.attachmentRowsField?.id, values, allValues]
  )

  useEffect(() => {
    if (!fieldMap.receivedRowsField) return
    const nextRows = parseRows(bestReceivedRowsRaw)
    setReceivedRows((prev) =>
      bunkerRowsSignature(prev) === bunkerRowsSignature(nextRows) ? prev : nextRows
    )
    setRowsHydrated(true)
  }, [fieldMap.receivedRowsField?.id, bestReceivedRowsRaw])

  const updateField = (field: Fields | undefined, value: string, isUserInitiated: boolean = true) => {
    if (!field) return
    onChange(field.label, String(field.id), value, isUserInitiated)
  }

  const updateRows = (nextRows: BunkerReceivedRow[]) => {
    const normalizedRows = nextRows.length > 0 ? nextRows : [createEmptyBunkerRow()]
    setReceivedRows(normalizedRows)

    // Keep legacy required bunker quantity fields in sync with first received row.
    const firstRow = normalizedRows[0]
    updateField(fieldMap.quantityDateField, firstRow?.dateUtc || '')
    updateField(fieldMap.quantityTimeField, firstRow?.timeUtc || '')

    const hfoTotal = normalizedRows.reduce((acc, row) => {
      const fuelType = getRowFuelDescriptor(row)
      const value = getEffectiveAllocatedQuantity(row)
      if (fuelType.includes('HFO') || fuelType.includes('HSFO') || fuelType.includes('LSFO')) {
        return acc + value
      }
      return acc
    }, 0)

    const mgoTotal = normalizedRows.reduce((acc, row) => {
      const fuelType = getRowFuelDescriptor(row)
      const value = getEffectiveAllocatedQuantity(row)
      if (fuelType.includes('MGO') || fuelType.includes('MDO') || fuelType.includes('DIESEL')) {
        return acc + value
      }
      return acc
    }, 0)

    updateField(fieldMap.quantityHfoField, hfoTotal > 0 ? String(hfoTotal) : '')
    updateField(fieldMap.quantityMgoField, mgoTotal > 0 ? String(mgoTotal) : '')

    if (fieldMap.receivedRowsField) {
      const rowsForPersistence = normalizedRows.map((row) => ({
        ...row,
        fuelType: row.fuelType || row.grade || '',
        hfoStorage: row.storage,
        mgoStorage: row.storage,
        attachmentFileName: row.attachments?.[0]?.fileName || '',
        hfo: getRowFuelDescriptor(row).includes('HFO') ? String(getEffectiveAllocatedQuantity(row)) : '',
        mgo:
          getRowFuelDescriptor(row).includes('MGO') ||
          getRowFuelDescriptor(row).includes('MDO') ||
          getRowFuelDescriptor(row).includes('DIESEL')
            ? String(getEffectiveAllocatedQuantity(row))
            : '',
      }))
      updateField(fieldMap.receivedRowsField, JSON.stringify(rowsForPersistence))
    }
  }

  const updateReceivedRow = (index: number, updater: (row: BunkerReceivedRow) => BunkerReceivedRow) => {
    const nextRows = receivedRows.map((row, rowIndex) => (rowIndex === index ? updater(row) : row))
    updateRows(nextRows)
  }

  const updateReceivedCell = (index: number, key: keyof BunkerReceivedRow, value: string) => {
    const nextRows = receivedRows.map((row, rowIndex) =>
      rowIndex === index ? {...row, [key]: value} : row
    )
    updateRows(nextRows)
  }

  const updateFuelType = (index: number, fuelType: string) => {
    updateReceivedRow(index, (prevRow) => ({
      ...prevRow,
      fuelType,
      grade: fuelType,
    }))
  }

  const updateStorage = (index: number, storage: string) => {
    const nextRows = receivedRows.map((row, rowIndex) => {
      if (rowIndex !== index) return row
      return {
        ...row,
        storage,
      }
    })
    updateRows(nextRows)
  }

  const addReceivedRow = () => {
    if (!canWriteReport) return
    const blockIds = getReportBlockIds(receivedRows)
    const activeBlock = blockIds[blockIds.length - 1]
    const blockRows = receivedRows.filter((row) => Number(row.reportBlock || 1) === activeBlock)
    const lastRow = blockRows[blockRows.length - 1]
    if (!lastRow) {
      updateRows([{...createEmptyBunkerRow(), reportBlock: activeBlock}])
      return
    }

    const allocationKey = buildRowAllocationKey(lastRow)
    const groupedRows = blockRows.filter((row) => buildRowAllocationKey(row) === allocationKey)
    const anchorRow = groupedRows[0] || lastRow
    const totalQuantity = toNumeric(anchorRow.quantity || lastRow.quantity)
    const allocatedQuantity = groupedRows.reduce((sum, row) => sum + toNumeric(row.storageQuantity), 0)
    const remainingQuantity = Math.max(totalQuantity - allocatedQuantity, 0)

    const nextRow: BunkerReceivedRow = {
      ...createEmptyBunkerRow(),
      reportBlock: activeBlock,
      allocationOnly: true,
      dateUtc: lastRow.dateUtc,
      timeUtc: lastRow.timeUtc,
      port: lastRow.port,
      quantity: remainingQuantity > 0 ? String(remainingQuantity) : '',
      supplierName: lastRow.supplierName,
      sulfurPercentage: lastRow.sulfurPercentage,
      fuelType: lastRow.fuelType,
      grade: lastRow.fuelType,
    }

    updateRows([...receivedRows, nextRow])
  }

  const addNewReport = () => {
    if (!canWriteReport) return
    const blockIds = getReportBlockIds(receivedRows)
    const nextBlock = Math.max(...blockIds) + 1
    updateRows([...receivedRows, {...createEmptyBunkerRow(), reportBlock: nextBlock}])
  }

  const removeReceivedRow = (index: number) => {
    if (!canWriteReport) return
    const nextRows = receivedRows.filter((_, rowIndex) => rowIndex !== index)
    if (nextRows.length === 0) {
      updateRows([createEmptyBunkerRow()])
      return
    }

    const blockIds = getReportBlockIds(nextRows)
    const normalizedRows = nextRows.filter((row) => blockIds.includes(Number(row.reportBlock || 1)))
    updateRows(normalizedRows.length > 0 ? normalizedRows : [createEmptyBunkerRow()])
  }

  const fieldValue = (field?: Fields) => String(field ? values[field.id]?.value ?? '' : '')

  const fieldValueFromAnyStep = (field?: Fields) => {
    if (!field) return ''

    const local = values[field.id]?.value
    if (local !== undefined && local !== null) return String(local)

    if (!allValues) return ''

    for (const tabData of Object.values(allValues)) {
      const candidate = tabData?.[field.id]?.value
      if (candidate !== undefined && candidate !== null) {
        return String(candidate)
      }
    }

    return ''
  }

  const yearValue = fieldValue(fieldMap.yearField)
  const vesselValue = fieldValue(fieldMap.vesselField)
  const quantityDateValue = fieldValueFromAnyStep(fieldMap.quantityDateField)
  const quantityTimeValue = fieldValueFromAnyStep(fieldMap.quantityTimeField)
  const quantityHfoValue = fieldValueFromAnyStep(fieldMap.quantityHfoField)
  const quantityMgoValue = fieldValueFromAnyStep(fieldMap.quantityMgoField)
  const reportBlocks = useMemo(() => {
    const grouped = new Map<number, Array<{row: BunkerReceivedRow; index: number}>>()
    receivedRows.forEach((row, index) => {
      const block = Number(row.reportBlock || 1)
      if (!grouped.has(block)) grouped.set(block, [])
      grouped.get(block)?.push({row, index})
    })

    const entries = Array.from(grouped.entries()).sort((a, b) => a[0] - b[0])
    return entries.map(([block, rows]) => ({block, rows}))
  }, [receivedRows])

  const hasReceivedSection = Boolean(fieldMap.receivedRowsField)
  const hasAttachmentSection = Boolean(fieldMap.attachmentRowsField)

  const getDraftAttachmentDocName = (block: number) =>
    attachmentDocNameByBlock[block] || BUNKER_ATTACHMENT_TYPES[0]

  const getDraftAttachmentRemarks = (block: number) => attachmentRemarksByBlock[block] || ''

  const getDraftAttachmentFile = (block: number) => attachmentFileByBlock[block] || null

  const uploadedAttachmentBlockById = useMemo(() => {
    const map = new Map<number, number>()
    if (!fieldMap.attachmentRowsField) return map

    if (!bestAttachmentPayloadRaw) return map

    try {
      const parsed =
        typeof bestAttachmentPayloadRaw === 'string'
          ? JSON.parse(bestAttachmentPayloadRaw)
          : bestAttachmentPayloadRaw
      const uploaded = Array.isArray(parsed?.uploaded) ? parsed.uploaded : []
      uploaded.forEach((item: any) => {
        const id = Number(item?.id)
        if (!Number.isFinite(id)) return
        const block = Number(item?.reportBlock)
        map.set(id, Number.isFinite(block) && block > 0 ? block : 1)
      })
    } catch {
      // no-op
    }

    return map
  }, [fieldMap.attachmentRowsField?.id, bestAttachmentPayloadRaw])

  const syncAttachmentField = (
    attachments: ReportAttachment[],
    pending: PendingReportAttachment[]
  ) => {
    if (!fieldMap.attachmentRowsField) return

    const payload = {
      uploaded: attachments.map((item) => ({
        id: item.id,
        reportBlock: uploadedAttachmentBlockById.get(Number(item.id)) || 1,
        documentName: item.documentName || item.fileName,
        remarks: item.remarks || '',
        fileName: item.fileName,
      })),
      pending: pending.map((item) => ({
        reportBlock: item.reportBlock || 1,
        documentName: item.documentName,
        remarks: item.remarks,
        fileName: item.file?.name || item.fileName || '',
      })),
    }

    const nextPayload = JSON.stringify(payload)
    if (nextPayload === lastSyncedAttachmentJsonRef.current) return
    lastSyncedAttachmentJsonRef.current = nextPayload

    updateField(fieldMap.attachmentRowsField, nextPayload)
  }

  useEffect(() => {
    const currentYear = String(new Date().getFullYear())
    if (!fieldMap.yearField || yearValue) return
    updateField(fieldMap.yearField, currentYear)
  }, [fieldMap.yearField?.id, yearValue])

  useEffect(() => {
    if (!fieldMap.vesselField || vesselValue) return

    const crewId = Number(currentUser?.roleEntityId ?? 0)
    const fallbackVessel = String(currentUser?.vessel?.fleet_name ?? '').trim()
    let active = true

    const applyVessel = (name: string) => {
      if (!name || !fieldMap.vesselField) return
      if (!String(fieldValue(fieldMap.vesselField)).trim()) {
        updateField(fieldMap.vesselField, name)
      }
    }

    if (crewId) {
      void getCrewUnitById(crewId)
        .then((crew) => {
          if (!active) return
          const fetchedVessel = String(crew?.vessel?.fleet_name ?? '').trim()
          applyVessel(fetchedVessel || fallbackVessel)
        })
        .catch(() => {
          applyVessel(fallbackVessel)
        })

      return () => {
        active = false
      }
    }

    applyVessel(fallbackVessel)
  }, [currentUser?.roleEntityId, currentUser?.vessel?.fleet_name, fieldMap.vesselField?.id, vesselValue])

  useEffect(() => {
    let active = true

    const resolveVesselId = async (): Promise<number> => {
      const vesselIdFromUser = Number((currentUser as any)?.vessel?.id ?? currentUser?.vessel?.id ?? 0)
      if (Number.isFinite(vesselIdFromUser) && vesselIdFromUser > 0) return vesselIdFromUser

      const crewId = Number(currentUser?.roleEntityId ?? 0)
      if (!Number.isFinite(crewId) || crewId <= 0) return 0

      try {
        const crew = await getCrewUnitById(crewId)
        const vesselIdFromCrew = Number(crew?.vessel?.id ?? 0)
        return Number.isFinite(vesselIdFromCrew) && vesselIdFromCrew > 0 ? vesselIdFromCrew : 0
      } catch {
        return 0
      }
    }

    const loadStorageOptions = async () => {
      const vesselId = await resolveVesselId()
      if (!active || !vesselId) {
        setStorageOptions([])
        return
      }

      try {
        const tanksOption = await getTanksOption(vesselId)
        if (!active) return

        const blocks = Array.isArray(tanksOption?.blocks) ? tanksOption.blocks : []
        const fuelRows = blocks.flatMap((block) => (Array.isArray(block?.fuelRows) ? block.fuelRows : []))
        const dieselRows = blocks.flatMap((block) =>
          Array.isArray(block?.dieselRows) ? block.dieselRows : []
        )

        const mergedRows = [...fuelRows, ...dieselRows]
        setStorageOptions(buildStorageOptions(mergedRows))
      } catch {
        if (!active) return
        setStorageOptions([])
      }
    }

    void loadStorageOptions()

    return () => {
      active = false
    }
  }, [currentUser?.roleEntityId, (currentUser as any)?.vessel?.id, currentUser?.vessel?.id])

  useEffect(() => {
    if (!rowsHydrated) return
    if (!fieldMap.receivedRowsField) return
    if (!receivedRows.length) return

    const first = receivedRows[0]
    const nextFirst = {
      ...first,
      dateUtc: first.dateUtc || quantityDateValue,
      timeUtc: first.timeUtc || quantityTimeValue,
      fuelType:
        first.fuelType || first.grade || (quantityHfoValue ? 'HFO' : quantityMgoValue ? 'MGO' : ''),
      quantity: first.quantity || quantityHfoValue || quantityMgoValue,
    }

    const hasChanged =
      nextFirst.dateUtc !== first.dateUtc ||
      nextFirst.timeUtc !== first.timeUtc ||
      nextFirst.fuelType !== first.fuelType ||
      nextFirst.quantity !== first.quantity

    if (!hasChanged) return

    const nextRows = receivedRows.map((row, index) => (index === 0 ? nextFirst : row))
    updateRows(nextRows)
  }, [
    rowsHydrated,
    fieldMap.receivedRowsField?.id,
    quantityDateValue,
    quantityTimeValue,
    quantityHfoValue,
    quantityMgoValue,
    receivedRows,
  ])

  useEffect(() => {
    if (!reportId) {
      setExistingAttachments([])
      return
    }

    let active = true
    setAttachmentsLoading(true)
    listReportAttachments(reportId)
      .then((items) => {
        if (!active) return
        setExistingAttachments(items)
        syncAttachmentField(items, pendingAttachments)
      })
      .catch(() => {
        if (!active) return
        setExistingAttachments([])
      })
      .finally(() => {
        if (active) setAttachmentsLoading(false)
      })

    return () => {
      active = false
    }
  }, [reportId])

  useEffect(() => {
    pendingHydratedFromFieldRef.current = false
  }, [fieldMap.attachmentRowsField?.id, reportId])

  useEffect(() => {
    const normalized = normalizePendingAttachments(initialPendingAttachments)
    if (normalized.length > 0) {
      pendingHydratedFromFieldRef.current = true
    }
    setPendingAttachments((prev) => (samePendingAttachments(prev, normalized) ? prev : normalized))
  }, [initialPendingAttachments])

  useEffect(() => {
    if (!fieldMap.attachmentRowsField) return
    if (pendingHydratedFromFieldRef.current) return
    if (pendingAttachments.length > 0) return

    if (!bestAttachmentPayloadRaw) return

    try {
      const parsed =
        typeof bestAttachmentPayloadRaw === 'string'
          ? JSON.parse(bestAttachmentPayloadRaw)
          : bestAttachmentPayloadRaw
      const pending = Array.isArray(parsed?.pending) ? parsed.pending : []
      if (pending.length === 0) return

      const hydrated: PendingReportAttachment[] = pending.map((item: any, idx: number) => ({
        id: `hydrated_${normalizePendingReportBlock(item?.reportBlock)}_${normalize(
          item?.documentName || ''
        )}_${normalize(item?.fileName || '')}_${idx}`,
        reportBlock: normalizePendingReportBlock(item?.reportBlock),
        documentName: String(item?.documentName || ''),
        remarks: String(item?.remarks || ''),
        fileName: String(item?.fileName || ''),
      }))

      pendingHydratedFromFieldRef.current = true
      setPendingAttachments((prev) => (samePendingAttachments(prev, hydrated) ? prev : hydrated))
    } catch {
      // no-op: invalid json in persisted field should not break editing flow
    }
  }, [fieldMap.attachmentRowsField?.id, bestAttachmentPayloadRaw, pendingAttachments.length])

  useEffect(() => {
    const normalizedInitial = normalizePendingAttachments(initialPendingAttachments)
    if (onPendingAttachmentsChange && !samePendingAttachments(pendingAttachments, normalizedInitial)) {
      onPendingAttachmentsChange(pendingAttachments)
    }
  }, [pendingAttachments, onPendingAttachmentsChange, initialPendingAttachments])

  useEffect(() => {
    syncAttachmentField(existingAttachmentsRef.current, pendingAttachments)
  }, [pendingAttachments, fieldMap.attachmentRowsField?.id])

  const addPendingAttachment = (reportBlock: number) => {
    if (!canWriteReport) return
    const selectedAttachmentFile = getDraftAttachmentFile(reportBlock)
    if (!selectedAttachmentFile) return

    const existingInBlock = existingAttachments.filter(
      (item) => (uploadedAttachmentBlockById.get(Number(item.id)) || 1) === reportBlock
    )
    const pendingInBlock = pendingAttachments.filter((item) => (item.reportBlock || 1) === reportBlock)

    const totalCount = existingInBlock.length + pendingInBlock.length
    if (totalCount >= BUNKER_ATTACHMENT_TYPES.length) {
      toast.warning(
        `Only ${BUNKER_ATTACHMENT_TYPES.length} attachments are allowed: ${BUNKER_ATTACHMENT_TYPES.join(
          ', '
        )}.`
      )
      return
    }

    const selectedType = getDraftAttachmentDocName(reportBlock).trim()
    const alreadyExists = [...existingInBlock, ...pendingInBlock].some(
      (item: any) => normalize(item?.documentName || '') === normalize(selectedType)
    )
    if (alreadyExists) {
      toast.warning(`${selectedType} already added. Only one file is allowed per attachment type.`)
      return
    }

    const next: PendingReportAttachment = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      reportBlock,
      documentName: selectedType,
      remarks: getDraftAttachmentRemarks(reportBlock).trim(),
      file: selectedAttachmentFile,
      fileName: selectedAttachmentFile.name,
    }

    setPendingAttachments((prev) => [...prev, next])
    setAttachmentDocNameByBlock((prev) => ({...prev, [reportBlock]: BUNKER_ATTACHMENT_TYPES[0]}))
    setAttachmentRemarksByBlock((prev) => ({...prev, [reportBlock]: ''}))
    setAttachmentFileByBlock((prev) => ({...prev, [reportBlock]: null}))
  }

  const removePendingAttachment = (id: string) => {
    if (!canWriteReport) return
    setPendingAttachments((prev) => prev.filter((item) => item.id !== id))
  }

  const viewPendingAttachment = (item: PendingReportAttachment) => {
    if (!canReadReport) return
    if (!item.file) return
    const objectUrl = URL.createObjectURL(item.file)
    window.open(objectUrl, '_blank', 'noopener,noreferrer')
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  }

  const removeExistingAttachment = async (attachmentId: number) => {
    if (!canWriteReport) return
    if (!reportId || !Number.isFinite(attachmentId) || attachmentId <= 0) return
    try {
      await deleteReportAttachment(reportId, attachmentId)
      const next = existingAttachments.filter((item) => Number(item.id) !== attachmentId)
      setExistingAttachments(next)
      syncAttachmentField(next, pendingAttachments)
    } catch (e) {
      console.error('Failed to remove attachment', e)
      toast.error('Could not remove this attachment. Try again or save the report first.')
    }
  }

  const attachmentRowId = (item: ReportAttachment) => {
    const n = Number(item.id)
    return Number.isFinite(n) && n > 0 ? n : 0
  }

  const openBlobInNewTab = (blob: Blob) => {
    const objectUrl = URL.createObjectURL(blob)
    window.open(objectUrl, '_blank', 'noopener,noreferrer')
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  }

  const downloadBlobAsFile = (blob: Blob, filename: string) => {
    const objectUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename || 'attachment'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
  }

  const viewUploadedAttachment = async (attachmentId: number) => {
    if (!canReadReport) return
    if (!reportId) return
    const blob = await fetchReportAttachmentViewBlob(reportId, attachmentId)
    openBlobInNewTab(blob)
  }

  const downloadUploadedAttachment = async (attachmentId: number, filename: string) => {
    if (!canReadReport) return
    if (!reportId) return
    const blob = await fetchReportAttachmentDownloadBlob(reportId, attachmentId)
    downloadBlobAsFile(blob, filename)
  }

  const getMatchingUploadedAttachment = (item: PendingReportAttachment) => {
    const docName = normalize(item.documentName)
    const fileName = normalize(item.fileName || item.file?.name || '')

    return existingAttachments.find((uploaded) => {
      const uploadedDocName = normalize(uploaded.documentName || '')
      const uploadedFileName = normalize(uploaded.fileName || '')
      return (
        (docName && uploadedDocName === docName) ||
        (fileName && uploadedFileName === fileName)
      )
    })
  }

  const viewPendingOrUploadedAttachment = async (item: PendingReportAttachment) => {
    if (!canReadReport) return
    if (item.file) {
      viewPendingAttachment(item)
      return
    }

    if (!reportId) return
    const matched = getMatchingUploadedAttachment(item)
    if (!matched?.id) return
    await viewUploadedAttachment(matched.id)
  }

  const downloadPendingOrUploadedAttachment = async (item: PendingReportAttachment) => {
    if (!canReadReport) return
    if (item.file) {
      const objectUrl = URL.createObjectURL(item.file)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = item.file.name
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
      return
    }

    if (!reportId) return
    const matched = getMatchingUploadedAttachment(item)
    if (!matched?.id) return
    await downloadUploadedAttachment(matched.id, matched.fileName)
  }

  return (
    <div className='px-3'>
      <h4 className='fw-bold text-dark py-3'>Bunker Report</h4>

      {hasReceivedSection && (
        <>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <h6 className='text-primary fw-bold text-uppercase mb-0'>Bunker Received</h6>
            <div className='d-flex align-items-center gap-2'>
              <button
                type='button'
                className='btn btn-sm btn-primary fw-semibold'
                disabled={!canWriteReport}
                onClick={addReceivedRow}
              >
                Add grade
              </button>
              <button
                type='button'
                className='btn btn-sm btn-light-primary fw-semibold'
                disabled={!canWriteReport}
                onClick={addNewReport}
              >
                New report
              </button>
            </div>
          </div>

          {reportBlocks.map((block, blockIndex) => (
            <div key={`bunker_block_${block.block}`} className='mb-4'>
              <div className='d-flex align-items-center justify-content-between mb-2'>
                <h6 className='fw-semibold text-dark mb-0'>Report {blockIndex + 1}</h6>
                <small className='text-muted'>Rows: {block.rows.length}</small>
              </div>

              <div className='table-responsive'>
                <table className='table table-bordered align-middle mb-0'>
                  <thead>
                    <tr>
                      <th style={{minWidth: 140}}>
                        <div>Date (UTC)</div>
                        <div className='text-muted small'>(dd/mm/yy)</div>
                      </th>
                      <th style={{minWidth: 130}}>
                        <div>Time (UTC)</div>
                        <div className='text-muted small'>(hh:mm)</div>
                      </th>
                      <th style={{minWidth: 180}}>Port</th>
                      <th style={{minWidth: 160}}>Fuel</th>
                      <th style={{minWidth: 120}}>Quantity (MT)</th>
                      <th style={{minWidth: 180}}>Supplier name</th>
                      <th style={{minWidth: 140}}>Sulfur percentage</th>
                      <th style={{minWidth: 160}}>Storage</th>
                      <th style={{minWidth: 150}}>Storage quantity</th>
                      <th style={{width: 90}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map(({row, index}) => (
                      <tr key={`row_${block.block}_${index}`}>
                        <td>
                          {row.allocationOnly ? (
                            <span className='text-muted small'>-</span>
                          ) : (
                            <input
                              type='date'
                              max={todayYmd}
                              className='form-control form-control-sm'
                              disabled={!canWriteReport}
                              value={row.dateUtc}
                              onChange={(e) => updateReceivedCell(index, 'dateUtc', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {row.allocationOnly ? (
                            <span className='text-muted small'>-</span>
                          ) : (
                            <input
                              type='time'
                              className='form-control form-control-sm'
                              disabled={!canWriteReport}
                              value={row.timeUtc}
                              onChange={(e) => updateReceivedCell(index, 'timeUtc', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {row.allocationOnly ? (
                            <span className='text-muted small'>-</span>
                          ) : (
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              disabled={!canWriteReport}
                              value={row.port}
                              onChange={(e) => updateReceivedCell(index, 'port', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {(() => {
                            const fuelOptions = ensureGradeOption([...BUNKER_FUEL_TYPES], row.fuelType)

                            if (row.allocationOnly) {
                              return <span className='text-muted small'>-</span>
                            }

                            return (
                              <SearchableSelect
                                className='form-control form-control-sm'
                                disabled={!canWriteReport}
                                value={row.fuelType}
                                onChange={(e) => updateFuelType(index, e.target.value)}
                              >
                                <option value=''>Select fuel</option>
                                {fuelOptions.map((fuel) => (
                                  <option key={`fuel_${index}_${fuel}`} value={fuel}>
                                    {fuel}
                                  </option>
                                ))}
                              </SearchableSelect>
                            )
                          })()}
                        </td>
                        <td>
                          {row.allocationOnly ? (
                            <span className='text-muted small'>-</span>
                          ) : (
                            <input
                              type='number'
                              step='any'
                              className='form-control form-control-sm'
                              disabled={!canWriteReport}
                              value={row.quantity}
                              onChange={(e) => updateReceivedCell(index, 'quantity', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {row.allocationOnly ? (
                            <span className='text-muted small'>-</span>
                          ) : (
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              disabled={!canWriteReport}
                              value={row.supplierName}
                              onChange={(e) => updateReceivedCell(index, 'supplierName', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {row.allocationOnly ? (
                            <span className='text-muted small'>-</span>
                          ) : (
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              disabled={!canWriteReport}
                              value={row.sulfurPercentage}
                              onChange={(e) => updateReceivedCell(index, 'sulfurPercentage', e.target.value)}
                            />
                          )}
                        </td>
                        <td>
                          {(() => {
                            const rowStorageOptions = ensureStorageOption(storageOptions, row.storage, row.grade)
                            return (
                              <SearchableSelect
                                className='form-control form-control-sm'
                                disabled={!canWriteReport}
                                value={row.storage}
                                onChange={(e) => updateStorage(index, e.target.value)}
                              >
                                <option value=''>Select storage</option>
                                {rowStorageOptions.map((option) => (
                                  <option key={`storage_${option.storage}`} value={option.storage}>
                                    {option.storage}
                                  </option>
                                ))}
                              </SearchableSelect>
                            )
                          })()}
                        </td>
                        <td>
                          <input
                            type='number'
                            step='any'
                            className='form-control form-control-sm'
                            disabled={!canWriteReport}
                            value={row.storageQuantity}
                            onChange={(e) => updateReceivedCell(index, 'storageQuantity', e.target.value)}
                          />
                        </td>
                        <td className='text-center'>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            disabled={receivedRows.length === 1 || !canWriteReport}
                            onClick={() => removeReceivedRow(index)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasAttachmentSection && (
                <div className='mt-3'>
                  <h6 className='text-primary fw-bold text-uppercase mb-3'>Attachments</h6>

                  <div className='row g-3 align-items-end mb-3'>
                    <div className='col-md-3'>
                      <label className='form-label mb-1'>Attachment type</label>
                      <SearchableSelect
                        className='form-select form-select-sm'
                        disabled={!canWriteReport}
                        value={getDraftAttachmentDocName(block.block)}
                        onChange={(e) =>
                          setAttachmentDocNameByBlock((prev) => ({...prev, [block.block]: e.target.value}))
                        }
                      >
                        {BUNKER_ATTACHMENT_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                    <div className='col-md-3'>
                      <label className='form-label mb-1'>Remarks</label>
                      <input
                        type='text'
                        className='form-control form-control-sm'
                        disabled={!canWriteReport}
                        value={getDraftAttachmentRemarks(block.block)}
                        onChange={(e) =>
                          setAttachmentRemarksByBlock((prev) => ({...prev, [block.block]: e.target.value}))
                        }
                      />
                    </div>
                    <div className='col-md-4'>
                      <label className='form-label mb-1'>File</label>
                      <input
                        type='file'
                        className='form-control form-control-sm'
                        disabled={!canWriteReport}
                        onChange={(e) =>
                          setAttachmentFileByBlock((prev) => ({
                            ...prev,
                            [block.block]: e.target.files?.[0] || null,
                          }))
                        }
                      />
                    </div>
                    <div className='col-md-2'>
                      <button
                        type='button'
                        className='btn btn-sm btn-primary w-100'
                        disabled={!canWriteReport}
                        onClick={() => addPendingAttachment(block.block)}
                      >
                        Add attachment
                      </button>
                    </div>
                  </div>

                  {attachmentsLoading && <div className='text-muted small mb-2'>Loading attachments...</div>}

                  <div className='table-responsive'>
                    <table className='table table-bordered align-middle mb-0'>
                      <thead>
                        <tr>
                          <th style={{minWidth: 240}}>Document</th>
                          <th>Remarks</th>
                          <th style={{minWidth: 180}}>File name</th>
                          <th style={{width: 220}}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {existingAttachments
                          .filter(
                            (item) =>
                              (uploadedAttachmentBlockById.get(Number(item.id)) || 1) === block.block
                          )
                          .map((item) => (
                            <tr key={`existing_attachment_${block.block}_${item.id}`}>
                              <td>{item.documentName || '--'}</td>
                              <td>{item.remarks || '--'}</td>
                              <td>{item.fileName || '--'}</td>
                              <td>
                                <div className='d-flex gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    disabled={!canReadReport}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      const aid = attachmentRowId(item)
                                      if (!reportId || !aid) {
                                        toast.info('Save the report as draft first to view uploads.')
                                        return
                                      }
                                      void viewUploadedAttachment(aid)
                                    }}
                                  >
                                    View
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-success'
                                    disabled={!canReadReport}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      const aid = attachmentRowId(item)
                                      if (!reportId || !aid) {
                                        toast.info('Save the report as draft first to download uploads.')
                                        return
                                      }
                                      void downloadUploadedAttachment(aid, item.fileName)
                                    }}
                                  >
                                    Download
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-danger'
                                    disabled={!canWriteReport}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      const aid = attachmentRowId(item)
                                      if (!reportId || !aid) {
                                        toast.info('Save the report as draft first to remove uploads.')
                                        return
                                      }
                                      void removeExistingAttachment(aid)
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}

                        {pendingAttachments
                          .filter((item) => (item.reportBlock || 1) === block.block)
                          .map((item) => (
                            <tr key={`${block.block}_${item.id}`}>
                              <td>{item.documentName || '--'}</td>
                              <td>{item.remarks || '--'}</td>
                              <td>{item.file?.name || item.fileName || '--'}</td>
                              <td>
                                <div className='d-flex gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    disabled={!canReadReport}
                                    onClick={() => void viewPendingOrUploadedAttachment(item)}
                                  >
                                    View
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-success'
                                    disabled={!canReadReport}
                                    onClick={() => void downloadPendingOrUploadedAttachment(item)}
                                  >
                                    Download
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-danger'
                                    disabled={!canWriteReport}
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      removePendingAttachment(item.id)
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}

                        {existingAttachments.filter(
                          (item) =>
                            (uploadedAttachmentBlockById.get(Number(item.id)) || 1) === block.block
                        ).length === 0 &&
                          pendingAttachments.filter((item) => (item.reportBlock || 1) === block.block).length ===
                            0 && (
                            <tr>
                              <td colSpan={4} className='text-center text-muted small py-4'>
                                No attachments added.
                              </td>
                            </tr>
                          )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {fieldMap.extraFields.length > 0 && (
        <div className='row gx-5 gy-4'>
          {fieldMap.extraFields.map((f) => (
            <div key={f.id} className='col-md-4'>
              <div className='text-muted mb-1'>{f.label}</div>
              <input
                type={f.fieldType.toLowerCase() === 'number' ? 'number' : 'text'}
                className='form-control grey-input'
                disabled={!canWriteReport}
                value={values[f.id]?.value ?? ''}
                onChange={(e) => onChange(f.label, String(f.id), e.target.value)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default BunkerReportStep
